const MEGABYTE = 1024 * 1024;

/**
 * Budget of the whole `POST /api/bugs` multipart body. Vercel refuses a
 * function request over 4.5 MB with `413 FUNCTION_PAYLOAD_TOO_LARGE` before
 * the route runs, which lost the entire bug report (text included) whenever a
 * recording was attached (seen on AB#29967). The margin covers the text
 * fields; each attachment's multipart framing is charged per part below.
 */
export const BUG_REPORT_PAYLOAD_LIMIT_BYTES = 4 * MEGABYTE;

/** Estimate of one attachment's multipart framing (boundary + part headers). */
export const MULTIPART_PART_OVERHEAD_BYTES = 512;

export interface PayloadSplit {
  images: File[];
  files: File[];
  /** Attachments that do not fit; the user attaches them in Azure DevOps. */
  omitted: File[];
}

/**
 * Splits the attachments into the ones that fit in the request budget and the
 * ones left out, filling it in order: images first, then files. `reservedBytes`
 * is what the rest of the body already takes (context file, text fields).
 */
export function splitAttachmentsByPayloadLimit(
  reservedBytes: number,
  images: File[],
  files: File[]
): PayloadSplit {
  let remaining = BUG_REPORT_PAYLOAD_LIMIT_BYTES - reservedBytes;
  const omitted: File[] = [];
  const fit = (list: File[]) =>
    list.filter((attachment) => {
      const cost = attachment.size + MULTIPART_PART_OVERHEAD_BYTES;
      if (cost > remaining) {
        omitted.push(attachment);
        return false;
      }
      remaining -= cost;
      return true;
    });

  const keptImages = fit(images);
  const keptFiles = fit(files);
  return { images: keptImages, files: keptFiles, omitted };
}

/**
 * Whether the rest of the body (context file, text fields) alone is over the
 * budget: leaving every attachment out would still be refused with a 413.
 */
export function exceedsPayloadLimit(reservedBytes: number): boolean {
  return reservedBytes > BUG_REPORT_PAYLOAD_LIMIT_BYTES;
}
